import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow-lg rounded-lg p-8">
          <h1 className="text-3xl font-bold text-blue-600 border-b-2 border-blue-600 pb-4 mb-6">
            Athena Project Management Privacy Policy
          </h1>
          
          <p className="text-gray-600 italic mb-8">
            Effective Date: January 13, 2026
          </p>
          
          <div className="prose prose-lg max-w-none">
            <p className="mb-6">
              Pantheon Development Company LLC ("Company," "we," "our," or "us") respects your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard information when you use the Athena Project Management software platform, including our mobile applications, websites, APIs, and related services (collectively, the "Service").
            </p>
            <p className="mb-6">
              By accessing or using Athena Project Management, you agree to this Privacy Policy.
            </p>

            <h2 className="text-2xl font-semibold text-blue-800 mt-8 mb-4">1. Scope</h2>
            <p className="mb-4">This policy applies to:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Athena Project Management web application</li>
              <li>Athena Project Management iOS and Android mobile apps</li>
              <li>Backend systems and APIs</li>
              <li>Administrative and support tools</li>
            </ul>

            <h2 className="text-2xl font-semibold text-blue-800 mt-8 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-blue-700 mt-6 mb-3">A. Account Information</h3>
            <p className="mb-4">Accounts are provisioned by organizations. We collect:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Name</li>
              <li>Email address</li>
              <li>Role and permissions</li>
              <li>Organization details</li>
              <li>Login credentials</li>
            </ul>
            <p className="mb-6 text-gray-600 italic">Users cannot self-register.</p>

            <h3 className="text-xl font-semibold text-blue-700 mt-6 mb-3">B. Subscriber Data</h3>
            <p className="mb-4">Data created or uploaded within Athena Project Management:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Tasks</li>
              <li>Projects</li>
              <li>Comments</li>
              <li>Files</li>
              <li>Client information</li>
              <li>Communications</li>
            </ul>

            <h3 className="text-xl font-semibold text-blue-700 mt-6 mb-3">C. Device & Usage Data</h3>
            <p className="mb-4">We collect:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>IP address</li>
              <li>Device identifiers</li>
              <li>Browser/app version</li>
              <li>Operating system</li>
              <li>Access timestamps</li>
            </ul>

            <h3 className="text-xl font-semibold text-blue-700 mt-6 mb-3">D. Crash & Diagnostics Data</h3>
            <p className="mb-4">We use monitoring tools (including Firebase Crashlytics) to collect:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Crash reports</li>
              <li>Stack traces</li>
              <li>Device type</li>
              <li>OS version</li>
            </ul>
            <p className="mb-6 text-gray-600 italic">Used solely to improve reliability and performance.</p>

            <h3 className="text-xl font-semibold text-blue-700 mt-6 mb-3">E. AI Feature Data</h3>
            <p className="mb-4">If AI features are used:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Inputs are processed automatically</li>
              <li>Outputs may contain inaccuracies</li>
              <li>We do not manually review AI outputs</li>
            </ul>
            <p className="mb-6 font-medium">You are responsible for verifying AI content before use.</p>

            <h3 className="text-xl font-semibold text-blue-700 mt-6 mb-3">F. Support Communications</h3>
            <p className="mb-4">We collect:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Support tickets</li>
              <li>Emails</li>
              <li>Chat transcripts</li>
            </ul>

            <h2 className="text-2xl font-semibold text-blue-800 mt-8 mb-4">3. How We Use Information</h2>
            <p className="mb-4">We use information to:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Operate and maintain Athena Project Management</li>
              <li>Authenticate users</li>
              <li>Provide customer support</li>
              <li>Improve performance</li>
              <li>Secure the platform</li>
              <li>Send service-related notifications</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h2 className="text-2xl font-semibold text-blue-800 mt-8 mb-4">4. Administrative Access (Important)</h2>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="mb-4 font-medium">Athena Project Management includes a Super Admin system.</p>
              <p className="mb-4">Authorized Company personnel may access customer accounts to:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Troubleshoot issues</li>
                <li>Provide technical support</li>
                <li>Investigate security incidents</li>
                <li>Maintain the platform</li>
              </ul>
              <p className="mb-2 font-medium">Safeguards:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Role-restricted access</li>
                <li>Logged and audited actions</li>
                <li>Confidentiality obligations</li>
              </ul>
              <p className="font-medium">We never access customer data without a legitimate business purpose.</p>
            </div>

            <h2 className="text-2xl font-semibold text-blue-800 mt-8 mb-4">5. Data Sharing</h2>
            <p className="mb-4 font-medium">We do not sell personal data.</p>
            <p className="mb-4">We may share data with:</p>
            
            <h3 className="text-lg font-semibold text-blue-700 mt-4 mb-2">Service Providers</h3>
            <p className="mb-4">Infrastructure, hosting, analytics, payment, and support vendors.</p>
            
            <h3 className="text-lg font-semibold text-blue-700 mt-4 mb-2">Legal Requirements</h3>
            <p className="mb-4">When required by law or court order.</p>
            
            <h3 className="text-lg font-semibold text-blue-700 mt-4 mb-2">Business Transactions</h3>
            <p className="mb-4">Mergers, acquisitions, or asset sales.</p>
            
            <h3 className="text-lg font-semibold text-blue-700 mt-4 mb-2">Aggregated Data</h3>
            <p className="mb-6">De-identified usage statistics.</p>

            <h2 className="text-2xl font-semibold text-blue-800 mt-8 mb-4">6. Data Retention</h2>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Data retained while account is active</li>
              <li>After termination: Retained up to 30 days, then securely deleted unless legally required</li>
            </ul>

            <h2 className="text-2xl font-semibold text-blue-800 mt-8 mb-4">7. Security</h2>
            <p className="mb-4">We use:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Encryption in transit</li>
              <li>Role-based access</li>
              <li>Audit logging</li>
              <li>Secure infrastructure</li>
            </ul>
            <p className="mb-6 text-gray-600">No system is 100% secure. Users must protect login credentials.</p>

            <h2 className="text-2xl font-semibold text-blue-800 mt-8 mb-4">8. International Users</h2>
            <p className="mb-6">
              Athena Project Management is hosted in the United States. By using the Service, you consent to U.S. data processing.
            </p>

            <h2 className="text-2xl font-semibold text-blue-800 mt-8 mb-4">9. Children</h2>
            <p className="mb-6">
              Athena Project Management is not intended for users under 18.
            </p>

            <h2 className="text-2xl font-semibold text-blue-800 mt-8 mb-4">10. Your Rights</h2>
            <p className="mb-4">Depending on location, you may request:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Access</li>
              <li>Correction</li>
              <li>Deletion</li>
            </ul>
            <p className="mb-6">
              Email: <a href="mailto:info@pantheontechnologies.us" className="text-blue-600 hover:underline">info@pantheontechnologies.us</a>
            </p>

            <h2 className="text-2xl font-semibold text-blue-800 mt-8 mb-4">11. Updates</h2>
            <p className="mb-6">
              We may update this policy at any time. Updates will be posted publicly.
            </p>

            <h2 className="text-2xl font-semibold text-blue-800 mt-8 mb-4">12. Contact</h2>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="font-semibold">Pantheon Development Company LLC</p>
              <p>Email: <a href="mailto:info@pantheontechnologies.us" className="text-blue-600 hover:underline">info@pantheontechnologies.us</a></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
